import { useState } from 'react';
import { sortBy } from 'lodash';
import { FunctionalGroupPosition, FunctionalGroupReactionCenter, FunctionalGroupRule } from '@moleculeone/types';

import { styled } from '../../../../../stitches.config';
import { Button, Popover, SimpleSelect } from '../../../..';
import FunctionalGroupFilter from './FunctionalGroupFilter';
import { functionalGroupsBySmarts } from './functionalGroups';
import { QueryBuilderRuleProps } from '../../types';
import { RuleContainer } from '../RuleContainers';

export interface FunctionalGroupRuleValue {
  smarts: string[];
  include: boolean;
  position: string;
  reactionCenter: string;
}

interface ReactionCenterItem {
  value: FunctionalGroupReactionCenter;
  label: string;
}

interface SelectorItem {
  value: boolean;
  label: string;
}

interface PositionItem {
  value: FunctionalGroupPosition;
  label: string;
}

const SELECTOR_ITEMS: SelectorItem[] = [
  { label: 'not in', value: false },
  { label: 'in', value: true },
];

const POSITION_ITEMS: PositionItem[] = [
  { label: 'reactants and products', value: FunctionalGroupPosition.ALL },
  { label: 'products', value: FunctionalGroupPosition.PRODUCTS },
  { label: 'reactants', value: FunctionalGroupPosition.REACTANTS },
];

const REACTION_CENTER_ITEMS: ReactionCenterItem[] = [
  {
    label: 'within the reaction center',
    value: FunctionalGroupReactionCenter.WITHIN,
  },
  {
    label: 'outside the reaction center',
    value: FunctionalGroupReactionCenter.OUTSIDE,
  },
  { label: 'any part of molecule', value: FunctionalGroupReactionCenter.ANY },
];

const Image = styled('svg', {
  width: '80px',
  height: '80px',
});

function getSmartsString(smarts: string[]) {
  return sortBy(smarts).join(' ');
}

export function FunctionalGroupRuleField(props: QueryBuilderRuleProps<FunctionalGroupRule>) {
  const { state, onChange } = props;
  const { smarts, include, position, reactionCenter } = state.value;
  const smartsStr = getSmartsString(smarts);
  const group = functionalGroupsBySmarts[smartsStr];

  const [isPopoverOpen, setPopoverOpen] = useState(false);

  const imageId = (group?.image ?? group?.smarts.length === 1) ? group.smarts[0] : undefined;

  function onFilterChange(smarts: string[]) {
    const nextValue = { smarts, include, position, reactionCenter };
    onChange({ ...state, value: nextValue });
  }

  function onSelectorChange(selectorItem: SelectorItem) {
    const nextValue = {
      smarts,
      include: selectorItem.value,
      position,
      reactionCenter,
    };
    onChange({ ...state, value: nextValue });
  }

  function onPositionChange(positionItem: PositionItem) {
    const nextValue = {
      smarts,
      include,
      position: positionItem.value,
      reactionCenter,
    };
    onChange({ ...state, value: nextValue });
  }

  function onReactionCenterChange(reactionCenter: ReactionCenterItem) {
    const nextValue = {
      smarts,
      include,
      position,
      reactionCenter: reactionCenter.value,
    };
    onChange({ ...state, value: nextValue });
  }

  return (
    <RuleContainer>
      {imageId && (
        <Image viewBox="71.992 -534.698 416.356 300.393" preserveAspectRatio="xMidYMid">
          <use xlinkHref={`/functional-groups.svg#${imageId}`} />
        </Image>
      )}

      <Popover
        open={isPopoverOpen}
        trigger={
          <Button
            weight="minimal"
            size="small"
            text={group ? (group.displayName ?? group.name) : 'Functional group'}
            rightIcon="expand_more"
            onClick={() => setPopoverOpen(!isPopoverOpen)}
          />
        }
        triggerProps={{ asChild: true }}
        contentProps={{ asChild: true, css: { zIndex: 1, minWidth: 600 } }}
      >
        <FunctionalGroupFilter value={smarts} onChange={onFilterChange} />
      </Popover>

      <SimpleSelect
        items={SELECTOR_ITEMS}
        selectedItemValue={String(include)}
        onChange={onSelectorChange}
        placeholder="Selector"
        getItemValue={item => String(item.value)}
        getItemLabel={item => item.label}
      />

      <SimpleSelect
        items={POSITION_ITEMS}
        selectedItemValue={String(position)}
        onChange={onPositionChange}
        placeholder="Selector"
        getItemValue={item => String(item.value)}
        getItemLabel={item => item.label}
      />

      <SimpleSelect
        items={REACTION_CENTER_ITEMS}
        selectedItemValue={String(reactionCenter)}
        onChange={onReactionCenterChange}
        placeholder="Selector"
        getItemValue={item => String(item.value)}
        getItemLabel={item => item.label}
      />
    </RuleContainer>
  );
}
